import { EpisodeOfCare, QuestionnaireResponse } from '@medplum/fhirtypes';

/**
 * When the lead arrived, for the Intake and billing panel's Signup date row.
 *
 * `Patient` has no creation field, so this has always been derived. The derivation
 * used to be `EpisodeOfCare.period.start` alone, on the stated basis that "every
 * patient gets one as of v21" — they do not. Measured on staging: **29 of the 30
 * most recently updated patients have no `EpisodeOfCare`**, and because `PanelRow`
 * renders nothing on an empty value (the panel's absent-not-blank rule) the row was
 * simply missing for almost every lead.
 *
 * The eligibility submission is the second source, and a good one: `authored` is
 * the moment a D2C lead actually signed up, it does not move afterwards — unlike
 * the `resource-created-at` tag, which is a tag-applied-at stamp that resets — and
 * the panel already reads that response for the Service row.
 *
 * Neither source exists for a Healthie-imported patient, and the row stays absent
 * rather than inventing a date a rep would read as "when this lead arrived".
 */
export function deriveSignupDate(
  episodes: EpisodeOfCare[],
  eligibility: QuestionnaireResponse | undefined
): string | undefined {
  return earliestEpisodeStart(episodes) ?? eligibility?.authored;
}

/**
 * The *first* episode's start, not the active one's. Switching tracks closes one
 * episode and opens another, so the active episode's start is when the patient
 * changed track — reading it would reset a long-standing lead's signup date.
 */
function earliestEpisodeStart(episodes: EpisodeOfCare[]): string | undefined {
  const starts = episodes
    .map((episode) => episode.period?.start)
    .filter((start): start is string => Boolean(start))
    .sort();

  return starts[0];
}
